import { useState } from 'react';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import MenuButton from '@/components/MenuButton';
import Prompt from '@/components/Prompt';
import ExportCurrentOrderButton from '@/modules/order/components/ExportCurrentOrderButton';
import type { Order } from '@/modules/order/models/Order';
import { OrderStatus as OrderStatusModel } from '@/modules/order/models/OrderStatus';
import type { PrintOrderOptions } from '@/modules/order/pages/OrderDetails';
import { OrderListPath } from '@/modules/order/paths';
import { ArrowLeftIcon, PencilIcon, PrinterIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SupplierOrderDetailsNavProps {
  order: Order;
  isEditing: boolean;
  onPrintOrder: (options?: PrintOrderOptions) => void;
  onDiscardChangesClick: () => void;
  onUpdateAndAcceptOrder: () => Promise<void>;
  onDispatchOrder: () => Promise<void>;
  onCompleteOrder: () => Promise<void>;
  onRejectOrder: (reason: string) => Promise<void>;
  onEditClick: () => void;
  onAcceptOrder: () => Promise<void>;
}

enum PrintOrderOption {
  Order = 'ORDER',
  DeliveryOrder = 'DELIVERY_ORDER',
}

const printOrderOptions = [
  { label: 'Order', value: PrintOrderOption.Order },
  { label: 'Delivery Order', value: PrintOrderOption.DeliveryOrder },
];

const SupplierPrintOrderButton = ({
  onPrintOrder,
}: Pick<SupplierOrderDetailsNavProps, 'onPrintOrder'>) => (
  <MenuButton
    data-testid="order-details-print-button"
    label="Print"
    icon={<PrinterIcon className="w-5 h-5 mr-1" />}
    options={printOrderOptions}
    onClick={(option) => {
      onPrintOrder({ hidePriceData: option.value === PrintOrderOption.DeliveryOrder });
    }}
  />
);

const SupplierNavBaseActions = ({
  order,
  onPrintOrder,
}: Pick<SupplierOrderDetailsNavProps, 'order' | 'onPrintOrder'>) => {
  return (
    <>
      <Button
        data-testid="order-details-back-button"
        to={OrderListPath}
        icon={<ArrowLeftIcon className="w-5 h-5 mr-1" />}
        variant="secondary"
      >
        Back
      </Button>
      <SupplierPrintOrderButton onPrintOrder={onPrintOrder} />
      <ExportCurrentOrderButton orderId={order.id} />
    </>
  );
};

const SupplierEditingNavActions = ({
  order,
  onPrintOrder,
  onDiscardChangesClick,
  onUpdateAndAcceptOrder,
}: Pick<
  SupplierOrderDetailsNavProps,
  'order' | 'onPrintOrder' | 'onDiscardChangesClick' | 'onUpdateAndAcceptOrder'
>) => (
  <>
    <SupplierPrintOrderButton onPrintOrder={onPrintOrder} />

    <Button
      onClick={onDiscardChangesClick}
      icon={<XMarkIcon className="w-5 h-5 mr-1" />}
      variant="secondary"
    >
      Discard Changes
    </Button>

    <Confirm
      onConfirm={onUpdateAndAcceptOrder}
      title="Accept Order"
      message={`Are you sure you want to update and accept order #${order.code}?`}
    >
      {(handleClick, loading) => (
        <Button
          data-testid="order-details-update-and-accept-button"
          onClick={handleClick}
          variant="primary"
          loading={loading}
        >
          Update and Accept Order
        </Button>
      )}
    </Confirm>
  </>
);

const SupplierAcceptedNavActions = ({
  order,
  onPrintOrder,
  onDispatchOrder,
}: Pick<SupplierOrderDetailsNavProps, 'order' | 'onPrintOrder' | 'onDispatchOrder'>) => (
  <>
    <SupplierNavBaseActions order={order} onPrintOrder={onPrintOrder} />
    <Confirm
      onConfirm={onDispatchOrder}
      title="Dispatch Order"
      message={`Are you sure you want to update the status to dispatched for order #${order.code}?`}
    >
      {(handleClick, loading) => (
        <Button
          data-testid="order-details-dispatch-button"
          onClick={handleClick}
          variant="primary"
          loading={loading}
        >
          Order Dispatched
        </Button>
      )}
    </Confirm>
  </>
);

const SupplierDisabledCompleteOrderButton = () => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span
      className="relative inline-flex"
      tabIndex={0}
      aria-describedby="order-details-complete-disabled-tooltip"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
    >
      <Button data-testid="order-details-complete-button" disabled>
        Complete Order
      </Button>
      {showTooltip && (
        <span
          id="order-details-complete-disabled-tooltip"
          role="tooltip"
          className="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white opacity-90 shadow-sm"
        >
          This order can be completed after it is marked as received.
        </span>
      )}
    </span>
  );
};

const SupplierDispatchedNavActions = ({
  order,
  onPrintOrder,
}: Pick<SupplierOrderDetailsNavProps, 'order' | 'onPrintOrder'>) => (
  <>
    <SupplierNavBaseActions order={order} onPrintOrder={onPrintOrder} />
    <SupplierDisabledCompleteOrderButton />
  </>
);

const SupplierReceivedNavActions = ({
  order,
  onPrintOrder,
  onCompleteOrder,
}: Pick<SupplierOrderDetailsNavProps, 'order' | 'onPrintOrder' | 'onCompleteOrder'>) => (
  <>
    <SupplierNavBaseActions order={order} onPrintOrder={onPrintOrder} />
    <Confirm
      onConfirm={onCompleteOrder}
      title="Complete Order"
      message={`Are you sure you want to complete order #${order.code}?`}
    >
      {(handleClick, loading) => (
        <Button
          data-testid="order-details-complete-button"
          onClick={handleClick}
          variant="primary"
          loading={loading}
        >
          Complete Order
        </Button>
      )}
    </Confirm>
  </>
);

const SupplierOpenNavActions = ({
  order,
  onPrintOrder,
  onRejectOrder,
  onEditClick,
  onAcceptOrder,
}: Pick<
  SupplierOrderDetailsNavProps,
  'order' | 'onPrintOrder' | 'onRejectOrder' | 'onEditClick' | 'onAcceptOrder'
>) => (
  <>
    <SupplierNavBaseActions order={order} onPrintOrder={onPrintOrder} />

    <Prompt
      onConfirm={onRejectOrder}
      title="Reject Order"
      message={`Are you sure you want to reject order #${order.code}?`}
    >
      {(handleClick, loading) => (
        <Button
          data-testid="order-details-reject-button"
          onClick={handleClick}
          icon={<XMarkIcon className="w-5 h-5 mr-1" />}
          variant="secondary"
          loading={loading}
        >
          Reject Order
        </Button>
      )}
    </Prompt>

    <Button
      data-testid="order-details-edit-button"
      onClick={onEditClick}
      icon={<PencilIcon className="w-5 h-5 mr-1" />}
      variant="secondary"
    >
      Edit Order
    </Button>

    <Confirm
      onConfirm={onAcceptOrder}
      title="Accept Order"
      message={`Are you sure you want to accept order #${order.code}?`}
    >
      {(handleClick, loading) => (
        <Button
          data-testid="order-details-accept-button"
          onClick={handleClick}
          variant="primary"
          loading={loading}
        >
          Accept Order
        </Button>
      )}
    </Confirm>
  </>
);

const SupplierOrderDetailsNav = ({
  order,
  isEditing,
  onPrintOrder,
  onDiscardChangesClick,
  onUpdateAndAcceptOrder,
  onDispatchOrder,
  onCompleteOrder,
  onRejectOrder,
  onEditClick,
  onAcceptOrder,
}: SupplierOrderDetailsNavProps) => {
  if (isEditing) {
    return (
      <>
        <Button
          data-testid="order-details-back-button"
          to={OrderListPath}
          icon={<ArrowLeftIcon className="w-5 h-5 mr-1" />}
          variant="secondary"
        >
          Back
        </Button>
        <SupplierEditingNavActions
          order={order}
          onPrintOrder={onPrintOrder}
          onDiscardChangesClick={onDiscardChangesClick}
          onUpdateAndAcceptOrder={onUpdateAndAcceptOrder}
        />
      </>
    );
  }

  if (order.status === OrderStatusModel.Completed) {
    return <SupplierNavBaseActions order={order} onPrintOrder={onPrintOrder} />;
  }

  if (order.status === OrderStatusModel.Accepted) {
    return (
      <SupplierAcceptedNavActions
        order={order}
        onPrintOrder={onPrintOrder}
        onDispatchOrder={onDispatchOrder}
      />
    );
  }

  if (order.status === OrderStatusModel.Received) {
    return (
      <SupplierReceivedNavActions
        order={order}
        onPrintOrder={onPrintOrder}
        onCompleteOrder={onCompleteOrder}
      />
    );
  }

  if (order.status === OrderStatusModel.Dispatched) {
    return <SupplierDispatchedNavActions order={order} onPrintOrder={onPrintOrder} />;
  }

  if (order.status !== OrderStatusModel.Rejected) {
    return (
      <SupplierOpenNavActions
        order={order}
        onPrintOrder={onPrintOrder}
        onRejectOrder={onRejectOrder}
        onEditClick={onEditClick}
        onAcceptOrder={onAcceptOrder}
      />
    );
  }

  return null;
};

export default SupplierOrderDetailsNav;
